import { IconButton, InputLabel, useTheme, Theme } from '@mui/material';
import { Send } from '@mui/icons-material';
import {
  CcfBackIcon,
  CcfBox,
  CcfButton,
  CcfDeboucedInput,
  CcfFavouriteIcon,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSelectedQReply,
  QuickReply,
  updateIsQReplySent,
  updatePreviewQuickReply,
  updateSentQReply,
} from '../../ccf-app-space.slice';
import styles from './ccf-quick-reply-preview.module.scss';

/* eslint-disable-next-line */
export interface CcfQuickReplyPreviewProps {}

/**
 * CcfQuickReplyPreview - used to display selected reply card
 * @param props - ? - CcfQuickReplyPreviewProps
 * @example - <CcfQuickReplyPreview />
 */
export function CcfQuickReplyPreview(props: CcfQuickReplyPreviewProps) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const type = theme.palette.mode;
  const selectedReply = useSelector(getSelectedQReply) as QuickReply;
  const [confirmationNumber, setConfirmationNumber] = React.useState('');
  const [confirmationNumberError, setConfirmationNumberError] =
    React.useState(false);
  const [translate] = useTranslator();

  /**
   * goToAllReplies - go back to all replies from preview reply panel
   * @param e -?- React.SyntheticEvent
   * @example - goToAllReplies(e)
   */
  const goToAllReplies = (e?: React.SyntheticEvent) => {
    dispatch(updatePreviewQuickReply(false));
  };

  /**
   * confirmation input change and blur event to set the confirmation number and validation of input
   * @param e -?- React.SyntheticEvent
   * @example -  confirmationNumberChange(e)
   */
  const confirmationNumberChange = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!(e.target as HTMLInputElement).value) {
      setConfirmationNumberError(true);
    } else {
      setConfirmationNumber((e.target as HTMLInputElement).value);
      setConfirmationNumberError(false);
    }
    return;
  };

  /**
   * Used to send selected reply to respective media interaction
   * @param event -? -React.SyntheticEvent
   * @example - onReplySend()
   */
  const onReplySend = (event?: React.SyntheticEvent) => {
    if (
      (selectedReply.isConfirmationNumberReq && confirmationNumber) ||
      !selectedReply.isConfirmationNumberReq
    ) {
      const qReplyContent = selectedReply.content1
        ? selectedReply.content1 +
          '\n\n' +
          (confirmationNumber ? '#' + confirmationNumber + '\n\n\n' : '') +
          (selectedReply.content2 ? selectedReply.content2 : '')
        : '';

      if (qReplyContent) dispatch(updateSentQReply(qReplyContent));
      dispatch(updateIsQReplySent(true));
      setConfirmationNumber('');
      goToAllReplies();
      setTimeout(() => {
        dispatch(updateIsQReplySent(false));
      }, 6000);
    }
    return;
  };

  return (
    <section>
      <CcfBox className={styles.backSection} onClick={goToAllReplies}>
        <IconButton className={styles.backBtn}>
          <CcfBackIcon className={styles.backIcon} />
        </IconButton>
        <CcfTypography className={styles.breadcrumbLabel}>
          {translate('allQReplies')}
        </CcfTypography>
      </CcfBox>
      <CcfBox className={styles.replyCardInfo}>
        <CcfTypography className={styles.replyTitle}>
          {selectedReply.title}
        </CcfTypography>
        <IconButton
          aria-label="favourite"
          classes={{
            root: styles.replyFavIcon,
          }}
        >
          <CcfFavouriteIcon
            className={
              selectedReply.isfavorite ? styles.favReply : styles.nonFavReply
            }
          />
        </IconButton>
      </CcfBox>
      <CcfBox className={styles.replyBodySection}>
        <CcfBox className={styles.replyBody}>
          <CcfTypography className={styles.replyContentBody}>
            {selectedReply.content1}
            {/* We accept any returned items with receipt. */}
          </CcfTypography>
        </CcfBox>
        {selectedReply.isConfirmationNumberReq && (
          <CcfBox component="div" className={styles.confirmationContainer}>
            <InputLabel
              htmlFor="confitmation-number"
              error={confirmationNumberError}
              className={styles.inputLabel}
            >
              {translate('confirmationNumber')} *{' '}
            </InputLabel>
            <CcfDeboucedInput
              id="confitmation-number"
              size="small"
              style={{
                background:
                  type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'white',
              }}
              variant="outlined"
              delay={1000}
              value={confirmationNumber}
              required={true}
              onChange={confirmationNumberChange}
              onBlur={confirmationNumberChange}
              error={confirmationNumberError}
            />
          </CcfBox>
        )}
        <CcfBox className={styles.replyBody}>
          <CcfTypography className={styles.replyContentBody}>
            {selectedReply.content2}
            {/* You will need to fill out a refund form in order to get compensation. */}
          </CcfTypography>
        </CcfBox>
      </CcfBox>

      <CcfBox display="flex" className={styles.sendButtonBox}>
        <CcfButton
          className={
            confirmationNumberError ||
            (!confirmationNumber && selectedReply.isConfirmationNumberReq)
              ? styles.sendBtn
              : styles.activeSend
          }
          onClick={onReplySend}
          id='agent_quickReply_send_button'
        >
          <Send />
        </CcfButton>
      </CcfBox>
    </section>
  );
}

export default CcfQuickReplyPreview;
